import {
  FormattingOperator,
  FormulaAction,
  FormulaJSON,
  isPercentOfUnitPriceAction,
  resolvedActionQuoteImpact,
} from './formula.types';

/**
 * A formula read out in plain words (Rod, 2026-10-09), used by the builder's Test Bench and the
 * product view so a formula reads the same everywhere:
 *   words   — "Weight × 10 = Shipping Cost"      /  "If Quantity ≥ 3 → 10%, otherwise 0%"
 *   numbers — "10 × 10 = 100 USD"                /  "Quantity = 3 → 10 %"
 * Steps apply left to right (the engine has no operator precedence), so an earlier + or − is
 * bracketed before a × or ÷, exactly as the engine computes. The result text is the caller's
 * (the engine's own number, or its error).
 */
export interface PlainFormulaOptions {
  name: string;
  /** Display label of a variable id ("Item.Custom.Weight" -> "Weight"). */
  label: (id: string) => string;
  /** The value a variable has in this reading (test value, or the product's own value). */
  value: (id: string) => number;
  /** "100 USD", or the error. */
  resultText: string;
  /** Translate a COMMON.* key. */
  t: (key: string) => string;
  /** Rounding chosen in the builder (kept outside the steps there); default: the formula's own. */
  formatting?: string;
  /** Whether a THEN/ELSE result is a percentage (the builder knows from its prompts). */
  isPercentAction?: (which: 'true' | 'false') => boolean;
}

const SYMBOLS: Record<string, string> = { add: '+', subtract: '−', multiply: '×', divide: '÷' };
const COMPARISON: Record<string, string> = {
  greater_than: '>',
  less_than: '<',
  equal_to: '=',
  greater_than_or_equal: '≥',
  less_than_or_equal: '≤',
};
const ROUNDING_KEYS: Record<string, string> = {
  [FormattingOperator.CEILING]: 'COMMON.Formula rounded up',
  [FormattingOperator.FLOOR]: 'COMMON.Formula rounded down',
  [FormattingOperator.ROUND]: 'COMMON.Formula rounded',
};

export function describeFormula(
  formula: FormulaJSON | null | undefined,
  o: PlainFormulaOptions,
): { words: string; numbers: string } | null {
  if (!formula) {
    return null;
  }
  const fmt = (n: number) => (Number.isFinite(n) ? String(Math.round(n * 10000) / 10000) : '?');
  const blank = (v: unknown) => v === undefined || v === null || v === '';
  const isNum = (v: unknown) =>
    typeof v === 'number' || (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v)));
  const label = (v: unknown) => (blank(v) ? '?' : isNum(v) ? fmt(Number(v)) : o.label(String(v)));
  const value = (v: unknown) => (blank(v) ? NaN : isNum(v) ? Number(v) : Number(o.value(String(v)) ?? 0));

  if (formula.logic_mode !== 'conditional') {
    const steps = formula.steps || [];
    const source = steps.find((s) => s.type === 'source');
    if (!source || blank(source.value)) {
      return null;
    }
    let words = label(source.value);
    let numbers = fmt(value(source.value));
    let lastWasAddSub = false;
    for (const step of steps.filter((s) => s.type === 'operation')) {
      const symbol = SYMBOLS[String(step.operator)] || '?';
      const mulDiv = symbol === '×' || symbol === '÷';
      if (mulDiv && lastWasAddSub) {
        words = `(${words})`;
        numbers = `(${numbers})`;
      }
      words += ` ${symbol} ${label(step.operand)}`;
      numbers += ` ${symbol} ${blank(step.operand) ? '?' : fmt(value(step.operand))}`;
      lastWasAddSub = !mulDiv;
    }
    const formatting = o.formatting ?? steps.find((s) => s.type === 'formatting')?.operator;
    const roundKey = ROUNDING_KEYS[String(formatting)];
    if (roundKey) {
      words = `${words}, ${o.t(roundKey)}`;
    }
    return { words: `${words} = ${o.name}`, numbers: `${numbers} = ${o.resultText}` };
  }

  const condition = formula.condition;
  if (!condition?.left || !condition?.operator || blank(condition.right)) {
    return null;
  }
  const isPercent = (which: 'true' | 'false', action?: FormulaAction) =>
    o.isPercentAction ? o.isPercentAction(which) : resolvedActionQuoteImpact(action, formula) === 'discount_percent';
  const actionText = (which: 'true' | 'false') => {
    const action = which === 'true' ? formula.true_action : formula.false_action;
    if (!action || blank(action.value)) {
      return '?';
    }
    if (isPercentOfUnitPriceAction(action)) {
      return `${label(action.value)}% × ${o.label('Item.Unit_Price')}`;
    }
    return `${label(action.value)}${isPercent(which, action) && isNum(action.value) ? '%' : ''}`;
  };
  const words =
    `${o.t('COMMON.Formula if')} ${label(condition.left)} ${COMPARISON[String(condition.operator)] || '?'} ` +
    `${label(condition.right)} → ${actionText('true')}, ${o.t('COMMON.Formula otherwise')} ${actionText('false')}`;
  const numbers = `${label(condition.left)} = ${fmt(value(condition.left))} → ${o.resultText}`;
  return { words, numbers };
}
